/**
 * Fills the product recommendations rail after the page has loaded.
 *
 * Two jobs, one file, because the product page needs both: it records the
 * product being viewed for the recently viewed rail, and it defines the element
 * that renders any of the three rails.
 *
 * Related and complementary products only exist through Shopify's
 * recommendations endpoint, and recently viewed only exists in this browser, so
 * neither can be rendered on the first pass. The element asks for its own
 * markup from the Section Rendering API once it comes into view.
 */

import { define } from 'custom-elements';

const STORAGE_KEY = 'lifelong:recently-viewed';
const HISTORY_LIMIT = 20;

/** Reads the stored history, tolerating a cleared or unreadable store. */
function readHistory() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    return Array.isArray(stored) ? stored.filter((id) => Number.isInteger(id)) : [];
  } catch {
    return [];
  }
}

/** Puts `id` at the front of the history, without duplicating it. */
function recordProduct(id) {
  if (!Number.isInteger(id)) return;

  try {
    const history = [id, ...readHistory().filter((seen) => seen !== id)];
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(0, HISTORY_LIMIT)));
  } catch {
    // Private browsing or a full store: the rail simply stays empty.
  }
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    this.url = this.getAttribute('data-url');
    this.source = this.getAttribute('data-source');
    this.limit = parseInt(this.getAttribute('data-limit'), 10) || 4;
    this.exclude = parseInt(this.getAttribute('data-exclude'), 10);

    if (!this.url || this.loaded) return;

    // The rail cannot watch for itself. It is empty until the fetch lands, an
    // empty rail is hidden so that it leaves no band behind, and an element
    // with no box never reports as intersecting -- so watching itself would
    // mean never loading and so never gaining a box. The section above it
    // stands in: once that is near, what follows is worth fetching.
    const anchor = this.closest('.shopify-section')?.previousElementSibling;
    if (!anchor) {
      this.load();
      return;
    }

    // Deliberately below the fold on every template it is used on, so the
    // fetch waits until it is nearly needed. Measured on scroll rather than
    // watched for, because a jump to the foot of the page -- the End key, or a
    // link to an anchor -- can pass the mark without a frame ever landing on it.
    const onScroll = () => {
      if (anchor.getBoundingClientRect().top > window.innerHeight + 600) return;
      window.removeEventListener('scroll', onScroll);
      this.load();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  get requestUrl() {
    if (this.source !== 'recently_viewed') return this.url;

    this.history = readHistory().filter((id) => id !== this.exclude);
    if (this.history.length === 0) return null;

    const ids = this.history.slice(0, this.limit);
    return `${this.url}&q=${encodeURIComponent(ids.map((id) => `id:${id}`).join(' OR '))}`;
  }

  async load() {
    const url = this.requestUrl;
    if (!url) return;

    this.loaded = true;

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      const rail = markup.querySelector('product-recommendations');
      if (!rail || rail.innerHTML.trim() === '') return;

      this.innerHTML = rail.innerHTML;
      if (this.source === 'recently_viewed') this.sortByHistory();
    } catch {
      // The rail is an extra, not part of the buying decision: leave it empty.
    }
  }

  /**
   * The search endpoint answers in relevance order, which for a "recently
   * viewed" rail is no order at all. Put the cards back in visit order.
   */
  sortByHistory() {
    const list = this.querySelector('.recommendations__list');
    if (!list) return;

    const items = [...list.children];
    items
      .slice()
      .sort((a, b) => {
        const rank = (item) => {
          const index = this.history.indexOf(parseInt(item.getAttribute('data-product-id'), 10));
          return index === -1 ? Number.MAX_SAFE_INTEGER : index;
        };
        return rank(a) - rank(b);
      })
      .forEach((item) => list.append(item));
  }
}

define('product-recommendations', ProductRecommendations);

const marker = document.querySelector('[data-recently-viewed-record]');
if (marker) recordProduct(parseInt(marker.getAttribute('data-recently-viewed-record'), 10));
